import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import max from 'date-fns/max';
import min from 'date-fns/min';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';

export function calculateDateIntervalOverlap(
  verticalMin: string | undefined,
  verticalMax: string | undefined,
  apiMin: string | undefined,
  apiMax: string | undefined,
): { minDate: string | undefined; maxDate: string | undefined; hasOverlap: boolean } {
  if (!verticalMin && !verticalMax && !apiMin && !apiMax) {
    return { minDate: undefined, maxDate: undefined, hasOverlap: true };
  }

  let finalMin: string | undefined;
  if (verticalMin && apiMin) {
    finalMin = verticalMin > apiMin ? verticalMin : apiMin;
  } else {
    finalMin = verticalMin || apiMin;
  }

  let finalMax: string | undefined;
  if (verticalMax && apiMax) {
    finalMax = verticalMax < apiMax ? verticalMax : apiMax;
  } else {
    finalMax = verticalMax || apiMax;
  }

  const hasOverlap = finalMin && finalMax ? finalMin <= finalMax : true;

  return {
    minDate: finalMin,
    maxDate: finalMax,
    hasOverlap,
  };
}

export function calculateStartDateConstraints(
  verticalMin: string | undefined,
  verticalMax: string | undefined,
  planMin: Date | undefined,
  planMaxExclusive: Date | undefined,
  now: Date = new Date(),
) {
  const verticalMinDate = verticalMin ? parseISO(verticalMin) : undefined;
  const verticalMaxExclusive = verticalMax ? addDays(parseISO(verticalMax), 1) : undefined;

  const earliestStartDate = verticalMinDate && planMin ? max([verticalMinDate, planMin]) : verticalMinDate ?? planMin;
  const latestStartDateExclusive =
    verticalMaxExclusive && planMaxExclusive
      ? min([verticalMaxExclusive, planMaxExclusive])
      : verticalMaxExclusive ?? planMaxExclusive;

  const firstAvailableInstant = earliestStartDate ? max([now, earliestStartDate]) : now;
  const hasAvailableStartDate = !latestStartDateExclusive || firstAvailableInstant < latestStartDateExclusive;

  return {
    earliestStartDate,
    latestStartDateExclusive,
    initialStartDate: hasAvailableStartDate ? firstAvailableInstant : undefined,
  };
}

export function calculateInitialStartDate(
  minDate: string | undefined,
  maxDate: string | undefined,
): string | undefined {
  const today = getTodayDateString();

  if (maxDate && maxDate < today) {
    return undefined;
  }

  if (minDate && minDate > today) {
    return minDate;
  }

  return today;
}

export function getTodayDateString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

export function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

export function isFutureStartDate(dateString: string | null): boolean {
  return Boolean(dateString && new Date(dateString) > new Date());
}

export function isStartDateValid(dateString: string | null): boolean {
  if (!dateString) {
    return false;
  }

  const date = new Date(dateString);
  if (date < new Date()) {
    return false;
  }

  if (isToday(date)) {
    return false;
  }

  return true;
}

export function doesLocalDateOverlapInterval(
  date: Date,
  earliestStartDate: Date | undefined,
  latestStartDateExclusive: Date | undefined,
): boolean {
  const localDayStart = startOfDay(date);
  const localDayEnd = addDays(localDayStart, 1);

  if (earliestStartDate && localDayEnd <= earliestStartDate) {
    return false;
  }

  if (latestStartDateExclusive && localDayStart >= latestStartDateExclusive) {
    return false;
  }

  return true;
}

export function clampToEarliestStartDate(date: Date, earliestStartDate: Date | undefined): Date {
  const localMidnight = startOfDay(date);
  return earliestStartDate && localMidnight < earliestStartDate ? earliestStartDate : localMidnight;
}

export function isDateInRange(date: Date, min: string | undefined, max: string | undefined): boolean {
  const dateString = format(date, 'yyyy-MM-dd');

  if (min && dateString < min) {
    return false;
  }

  if (max && dateString > max) {
    return false;
  }

  return true;
}
